import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/common/Common";
import { RealBadge, AIBadge } from "@/components/common/Badges";
import { useScope } from "@/context/ScopeContext";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { brl, num, pct } from "@/lib/format";

const SUGGESTIONS = ["Por que meu CPC aumentou?", "Quais palavras negativas devo adicionar?", "Analise minha campanha.", "Como melhorar minhas conversões?", "Por que minha campanha não está recebendo cliques?"];

const Facts = ({ f, error }) => (
  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="chat-real-data">
    <RealBadge />
    {f ? <p className="text-xs font-mono mt-2">{f.account} · {f.period}: {num(f.clicks)} cliques · {num(f.impressions)} impressões · CTR {pct(f.ctr)} · CPC {brl(f.cpc)} · {brl(f.cost)} · {num(f.conversions, 1)} conversões · {f.campaigns} campanhas</p>
      : <p className="text-xs mt-2 text-muted-foreground">{error || "Nenhuma conta selecionada — sem dados reais nesta resposta."}</p>}
  </div>
);

function md(text) {
  return text.split("\n").map((l, i) => {
    if (/^#{1,4}\s/.test(l)) return <p key={i} className="font-semibold mt-3">{l.replace(/^#+\s/, "")}</p>;
    if (/^\s*[-*]\s/.test(l)) return <p key={i} className="pl-4 -indent-3">• {l.replace(/^\s*[-*]\s/, "").replace(/\*\*/g, "")}</p>;
    return <p key={i} className="min-h-[0.5rem]">{l.replace(/\*\*/g, "")}</p>;
  });
}

export default function Assistant() {
  const { accountId } = useScope();
  const { reloadOrg } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get("/assistant/history").then((r) => setMsgs(r.data.reverse().map((m) => ({ q: m.question, a: m.answer, facts: m.facts })))).catch(() => {}); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    if (!text.trim() || busy) return;
    setQ(""); setBusy(true);
    setMsgs((m) => [...m, { q: text, a: "", facts: null, pending: true }]);
    const patch = (p) => setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, ...p(x) } : x)));
    try {
      const r = await fetch(`${API}/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, account_id: accountId || null }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(typeof d.detail === "string" ? d.detail : "Não foi possível responder."); }
      const reader = r.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const ev = (p.match(/^event: (.*)$/m) || [])[1] || "message"; const data = JSON.parse((p.match(/^data: (.*)$/m) || [])[1] || "{}");
          if (ev === "facts") patch(() => ({ facts: data.facts, factsError: data.error }));
          else if (ev === "error") throw new Error(data.error);
          else if (data.t) patch((x) => ({ a: x.a + data.t }));
        }
      }
    } catch (e) { toast.error(e.message); patch(() => ({ a: "", error: e.message })); }
    patch(() => ({ pending: false })); setBusy(false); reloadOrg();
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="AI Assistant" desc="Pergunte qualquer coisa sobre suas campanhas. Dados da conta e recomendações da IA aparecem sempre separados." />
      <div className="space-y-8 mb-40" data-testid="chat-messages">
        {!msgs.length && <div className="flex flex-wrap gap-2">{SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="px-4 py-2 rounded-full border text-sm hover:bg-secondary" data-testid="chat-suggestion">{s}</button>)}</div>}
        {msgs.map((m, i) => (
          <div key={i} className="space-y-3">
            <div className="flex justify-end"><p className="bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]">{m.q}</p></div>
            <div className="flex gap-3"><div className="h-8 w-8 rounded-full bg-indigo-500/10 text-indigo-500 grid place-items-center shrink-0"><Bot className="h-4 w-4" /></div>
              <div className="flex-1 space-y-3 min-w-0">
                {(m.facts !== undefined || !m.pending) && <Facts f={m.facts} error={m.factsError} />}
                <div className="ai-card p-4" data-testid="chat-ai-answer"><AIBadge /><div className="text-sm leading-relaxed mt-2">{m.error ? <span className="text-rose-500">{m.error}</span> : m.a ? md(m.a) : <span className="text-muted-foreground animate-pulse">Pensando...</span>}</div></div>
              </div></div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-72 lg:right-auto lg:w-[min(56rem,calc(100vw-20rem))] surface p-2 flex gap-2 shadow-xl z-30">
        <Textarea rows={1} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(q); } }} placeholder="Pergunte sobre suas campanhas..." className="resize-none border-0 focus-visible:ring-0 focus-visible:shadow-none min-h-[48px] text-base" data-testid="chat-input" />
        <Button disabled={busy || !q.trim()} className="rounded-full h-11 w-11 p-0 shrink-0" data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
